import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Bot, Building, ClipboardList, Handshake, Landmark, LifeBuoy, ShoppingCart, Users } from 'lucide-react';
import {
  combinarConfig,
  MODULOS_SEM_SUBSISTEMA,
  novoDesenhoLigado,
  paginaNoSubsistema,
  permissaoDoSubsistema,
  SUBSISTEMAS_ATIVOS,
  subsistemaLigado,
  type ConfigSubsistemas,
  type LinkExterno,
} from '@malgoos/shared';
import { abaVisivel, modulosWeb, paginasDoSubsistema, type ModuloWeb } from './modulos-web';
import { useSessao, type DadosSessao, type EmpresasDaSessao } from './sessao';

/**
 * D1 (sub-sistemas, L2): a escolha de empresa e sub-sistema no web. Sub-sistema é filtro de menu: a regra é a função única
 * do núcleo (`paginaNoSubsistema`, em `@malgoos/shared`); aqui só se decide QUAL sub-sistema vale agora.
 *  - Sem a configuração ligada (`ativo`), nada muda: sem tela de escolha e sem filtro (`naNavegacao` ausente).
 *  - Ligada: a pessoa usa os sub-sistemas ligados na empresa, com a permissão `subsistema.<x>.usar` e com ao menos uma página
 *    que ela pode ver. Com um só, abre direto nele; com mais, a tela inicial (`/subsistemas`) pede a escolha.
 *  - A escolha vale por aba do navegador e por empresa (sessionStorage); a última fica guardada por pessoa e por empresa no
 *    aparelho (localStorage) e volta marcada na tela inicial. Trocar de empresa pede a escolha de novo.
 *  - "Todas as empresas" mostra os sub-sistemas ligados em pelo menos uma das empresas permitidas.
 */

export const ICONES_SUBSISTEMAS: Record<string, LucideIcon> = {
  // Lista de 30/10 (item 13): o ADM (administrativo) estava sem ícone no seletor.
  adm: ClipboardList,
  erp: Landmark,
  crm: Handshake,
  suporte: LifeBuoy,
  automacoes: Bot,
  rh: Users,
  condominial: Building,
  pdv: ShoppingCart,
};

export type DadosSubsistemas = NonNullable<DadosSessao['subsistemas']>;

/**
 * Chave da empresa da escolha: a atual, "todas" sem filtro com várias empresas, ou "workspace" sem empresa. V2 verticais:
 * sem filtro e com uma empresa só, ela é a atual (a chave segue a mesma de antes).
 */
export const chaveDaEmpresa = (e?: EmpresasDaSessao) => e?.atual ?? (e?.todas ? 'todas' : 'workspace');

/** Configuração que vale agora: a do workspace com a da empresa atual por cima; sem filtro, ligado em ao menos uma. */
export function configEfetiva(dados: DadosSubsistemas | undefined, empresas?: EmpresasDaSessao): ConfigSubsistemas {
  const ws = dados?.workspace ?? {};
  if (!empresas) return ws;
  if (empresas.atual) return combinarConfig(ws, dados?.empresas[empresas.atual]);
  if (!empresas.todas) return ws;
  const ids = empresas.opcoes.map((o) => o.id);
  if (!ids.length) return ws;
  const desligados = SUBSISTEMAS_ATIVOS.filter((sub) => ids.every((id) => !subsistemaLigado(combinarConfig(ws, dados?.empresas[id]), sub)));
  return { ...ws, desligados };
}

interface AcessoPessoa {
  instalado(slug: string): boolean;
  pode(permissao: string): boolean;
  modo?(chave: string): string | undefined;
  plataforma?: boolean;
  todasAsEmpresas?: boolean;
  modoSimples?: boolean;
  /** Val1: página não negada (desmarcada no papel ou retirada da pessoa). */
  paginaLiberada?(slug: string, aba: string): boolean;
}

/**
 * Sub-sistemas que a pessoa usa nesta configuração: ligado, com a permissão de uso e com ao menos uma página visível a ela.
 * Val8: no novo desenho, a página da casca também conta (o CRM do novo desenho só tem as da casca).
 */
export function subsistemasDaPessoa(cfg: ConfigSubsistemas, acesso: AcessoPessoa, lista: ModuloWeb[] = modulosWeb): string[] {
  const sessao = { pode: (p: string) => acesso.pode(p), modo: (c: string) => acesso.modo?.(c), plataforma: acesso.plataforma, todasAsEmpresas: acesso.todasAsEmpresas, modoSimples: acesso.modoSimples };
  const novo = novoDesenhoLigado(cfg);
  return SUBSISTEMAS_ATIVOS.filter(
    (sub) =>
      subsistemaLigado(cfg, sub) &&
      acesso.pode(permissaoDoSubsistema(sub)) &&
      (lista.some((m) => acesso.instalado(m.slug) && !MODULOS_SEM_SUBSISTEMA.has(m.slug) && m.abas.some((a) => abaVisivel(a, sessao) && (acesso.paginaLiberada?.(m.slug, a.chave) ?? true) && paginaNoSubsistema(cfg, sub, m.slug, a.chave))) ||
        (novo && paginasDoSubsistema(acesso, sub, lista, cfg).length > 0)),
  );
}

/** Filtro da navegação no sub-sistema (null: só os módulos comuns, para quem não usa nenhum). */
export const filtroDoSubsistema = (cfg: ConfigSubsistemas, sub: string | null) => (slug: string, aba: string) => (sub ? paginaNoSubsistema(cfg, sub, slug, aba) : MODULOS_SEM_SUBSISTEMA.has(slug));

const PREFIXO_ATUAL = 'malgoos.subsistema.';
const chaveAtual = (usuarioId: string) => `${PREFIXO_ATUAL}${usuarioId}`;
const CHAVE_APOS_LOGIN = 'malgoos.subsistemas.aposlogin';

/**
 * Chamado no fim do login: a próxima tela pede a escolha (a tela inicial do plano 3.5), mesmo com o último guardado. Sem
 * esta marca (aba nova, recarga), vale a escolha da aba ou, sem ela, o último usado nesta empresa.
 */
export function marcarAposLogin() {
  try {
    for (const k of Object.keys(sessionStorage)) if (k.startsWith(PREFIXO_ATUAL)) sessionStorage.removeItem(k);
    sessionStorage.setItem(CHAVE_APOS_LOGIN, '1');
  } catch {
    /* sem storage: segue o último usado */
  }
}

function aposLogin(): boolean {
  try {
    return sessionStorage.getItem(CHAVE_APOS_LOGIN) === '1';
  } catch {
    return false;
  }
}
export const chaveUltimo = (usuarioId: string, empresa: string) => `malgoos.subsistema.ultimo.${usuarioId}.${empresa}`;

interface Escolha {
  empresa: string;
  sub: string;
}

function lerEscolha(usuarioId: string): Escolha | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(chaveAtual(usuarioId)) ?? 'null') as Partial<Escolha> | null;
    return v && typeof v.empresa === 'string' && typeof v.sub === 'string' ? { empresa: v.empresa, sub: v.sub } : null;
  } catch {
    return null;
  }
}

function lerUltimo(usuarioId: string, empresa: string): string | null {
  try {
    return localStorage.getItem(chaveUltimo(usuarioId, empresa));
  } catch {
    return null;
  }
}

export interface ValorSubsistema {
  /** A configuração está ligada no workspace. */
  ativo: boolean;
  config: ConfigSubsistemas;
  /** Sub-sistemas que a pessoa usa agora (na empresa atual). */
  opcoes: string[];
  /** D3: links externos que a pessoa vê (abrem em nova aba). */
  links: LinkExterno[];
  atual: string | null;
  /** Último escolhido nesta empresa (marcado na tela inicial). */
  ultimo: string | null;
  /** Ligada, sem escolha válida e com mais de uma opção: a tela inicial pede a escolha. */
  precisaEscolher: boolean;
  /** CRM novo desenho (N7): casca nova; a escolha fica no seletor do topo, sem a tela inicial. */
  novoDesenho: boolean;
  escolher(sub: string): void;
  /** Filtro da navegação (ausente sem a configuração ligada). */
  naNavegacao?: (slug: string, aba: string) => boolean;
}

/**
 * O sub-sistema que vale agora (função pura). Casca de sempre: a escolha da aba; com uma opção só, ela; em aba nova ou recarga
 * (sem escolha nesta aba e fora do pós-login), o último usado nesta empresa; senão nenhum (a tela inicial pede a escolha).
 * CRM novo desenho (N5): sem tela intermediária; a escolha da aba, o último usado ou o CRM (o primeiro, para quem não usa o
 * CRM), como o clone do RD, que abre no CRM.
 */
export function subsistemaAtual(p: { novoDesenho: boolean; valida: string | null; ultimo: string | null; opcoes: string[]; comEscolha: boolean; aposLogin: boolean }): string | null {
  if (p.valida) return p.valida;
  if (p.novoDesenho) return p.ultimo ?? (p.opcoes.includes('crm') ? 'crm' : (p.opcoes[0] ?? null));
  if (p.opcoes.length === 1) return p.opcoes[0]!;
  return !p.comEscolha && !p.aposLogin ? p.ultimo : null;
}

const INATIVO: ValorSubsistema = { ativo: false, config: {}, opcoes: [], links: [], atual: null, ultimo: null, precisaEscolher: false, novoDesenho: false, escolher: () => undefined };

const Ctx = createContext<ValorSubsistema | null>(null);

export function ProvedorSubsistema({ children }: { children: ReactNode }) {
  const s = useSessao();
  const usuarioId = s.usuario.id;
  const [escolha, setEscolha] = useState<Escolha | null>(() => lerEscolha(usuarioId));
  const empresa = chaveDaEmpresa(s.empresas);
  const config = useMemo(() => configEfetiva(s.subsistemas, s.empresas), [s.subsistemas, s.empresas]);
  const ativo = !!config.ativo;
  const { instalado, pode, modo, plataforma, todasAsEmpresas } = s;
  // `instalado`, `pode` e `modo` mudam de identidade a cada render da sessão; a lista depende do que eles leem.
  const opcoes = useMemo(
    () => (ativo ? subsistemasDaPessoa(config, { instalado, pode, modo, plataforma, todasAsEmpresas }) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as funções leem `permissoes`, `modulos` e `modos`
    [ativo, config, s.permissoes, s.modulos, s.modos, plataforma, todasAsEmpresas],
  );
  const escolher = useCallback(
    (sub: string) => {
      const nova = { empresa, sub };
      setEscolha(nova);
      try {
        sessionStorage.setItem(chaveAtual(usuarioId), JSON.stringify(nova));
        sessionStorage.removeItem(CHAVE_APOS_LOGIN);
        localStorage.setItem(chaveUltimo(usuarioId, empresa), sub);
      } catch {
        /* sem storage: vale até recarregar */
      }
    },
    [empresa, usuarioId],
  );
  const valor = useMemo<ValorSubsistema>(() => {
    if (!ativo) return INATIVO;
    const valida = escolha && escolha.empresa === empresa && opcoes.includes(escolha.sub) ? escolha.sub : null;
    const guardado = lerUltimo(usuarioId, empresa);
    const ultimo = guardado && opcoes.includes(guardado) ? guardado : null;
    // Aba nova ou recarga (sem escolha nesta aba e fora do pós-login): segue no último usado nesta empresa. Trocar de empresa
    // (escolha da aba em outra empresa) e o pós-login pedem a escolha na tela inicial.
    const novoDesenho = novoDesenhoLigado(config);
    const atual = subsistemaAtual({ novoDesenho, valida, ultimo, opcoes, comEscolha: !!escolha, aposLogin: aposLogin() });
    return {
      ativo,
      config,
      opcoes,
      links: config.links ?? [],
      atual,
      ultimo,
      precisaEscolher: !atual && opcoes.length > 1,
      novoDesenho,
      escolher,
      naNavegacao: filtroDoSubsistema(config, atual),
    };
  }, [ativo, config, escolha, empresa, escolher, opcoes, usuarioId]);
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

/**
 * Lista de 30/10 B (tela dividida, item 2 da ficha do Principal): o valor do sub-sistema de um painel da tela dividida. Mesma
 * configuração e mesmas opções da sessão; o atual é o do painel (fora das opções, o da aba) e escolher muda só o painel.
 */
export function comSubsistema(base: ValorSubsistema, atual: string | null, escolher: (sub: string) => void): ValorSubsistema {
  if (!base.ativo) return base;
  const valido = atual && base.opcoes.includes(atual) ? atual : base.atual;
  return { ...base, atual: valido, precisaEscolher: !valido && base.opcoes.length > 1, escolher, naNavegacao: filtroDoSubsistema(base.config, valido) };
}

/** Provedor com um valor já pronto (o do painel da tela dividida). */
export function ProvedorSubsistemaDoPainel({ valor, children }: { valor: ValorSubsistema; children: ReactNode }) {
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

/** Sub-sistema da sessão; fora do provedor (testes de componente), a navegação de sempre. */
export function useSubsistema(): ValorSubsistema {
  return useContext(Ctx) ?? INATIVO;
}
